// Tokenización de tarjeta contra Payway, desde el navegador del comprador.
//
// El número de tarjeta NUNCA llega a nuestro servidor: va directo de acá a
// Payway, que devuelve un token de un solo uso, y es ese token el que le
// mandamos a nuestra API para cobrar.
//
// Hablamos con `POST /api/v2/tokens` por fetch en vez de cargar el `decidir.js`
// oficial: ese SDK necesita un <form> con atributos `data-decidir` y lo lee del
// DOM, lo que en React obliga a un formulario oculto sincronizado a mano. El
// endpoint es un POST JSON plano y la única diferencia real es el device
// fingerprint de CyberSource, que sólo aplica con antifraude habilitado.

const BASE_URL = {
    sandbox: 'https://developers-ventasonline.payway.com.ar',
    production: 'https://ventasonline.payway.com.ar',
};

// Payway valida que el payment_method_id coincida con la tarjeta que se
// tokenizó, y qué ids tiene habilitados depende de cada comercio. Por eso el
// comprador elige la marca —es lo que hace el plugin oficial de WooCommerce— y
// el BIN sólo la pre-selecciona.
export const CARD_BRANDS = [
    { id: 1, label: 'Visa', pattern: /^4/ },
    { id: 104, label: 'Mastercard', pattern: /^(5[1-5]|2[2-7])/ },
    { id: 65, label: 'American Express', pattern: /^3[47]/, cvvLength: 4 },
    { id: 8, label: 'Diners', pattern: /^3(0[0-5]|[68])/ },
    { id: 63, label: 'Cabal', pattern: /^(58|6042|6043|6044|6045)/ },
    { id: 24, label: 'Naranja', pattern: /^(589562|402917|402918|527571|527572)/ },
];

export const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

export const detectBrand = (cardNumber) => {
    const digits = onlyDigits(cardNumber);
    if (digits.length < 2) return null;
    // Naranja y Cabal comparten prefijo con marcas más genéricas, así que gana
    // el patrón más específico.
    return (
        [...CARD_BRANDS]
            .sort((a, b) => b.pattern.source.length - a.pattern.source.length)
            .find((brand) => brand.pattern.test(digits)) || null
    );
};

// Luhn. Atajar acá una tarjeta mal tipeada evita gastar un intento contra el
// emisor: Visa y Mastercard multan los reintentos rechazados.
export const isValidCardNumber = (cardNumber) => {
    const digits = onlyDigits(cardNumber);
    if (digits.length < 13 || digits.length > 19) return false;

    let sum = 0;
    let double = false;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
        let digit = Number(digits[i]);
        if (double) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        sum += digit;
        double = !double;
    }
    return sum % 10 === 0;
};

export const isValidExpiry = (month, year) => {
    const m = Number(month);
    const y = Number(year);
    if (!(m >= 1 && m <= 12)) return false;
    if (!(y >= 0 && y <= 99)) return false;

    const now = new Date();
    const currentYear = now.getFullYear() % 100;
    const currentMonth = now.getMonth() + 1;

    if (y < currentYear) return false;
    if (y === currentYear && m < currentMonth) return false;
    return true;
};

export const formatCardNumber = (value) => {
    const digits = onlyDigits(value).slice(0, 19);
    // Amex se agrupa 4-6-5; el resto de a 4.
    if (/^3[47]/.test(digits)) {
        return [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)]
            .filter(Boolean)
            .join(' ');
    }
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

// Payway devuelve los errores de validación como una lista de {error:{type}}.
// Se traducen acá para no mostrarle jerga en inglés al comprador.
const TOKEN_ERRORS = {
    empty_card_number: 'Ingresá el número de la tarjeta.',
    nan_card_number: 'El número de la tarjeta debe tener sólo números.',
    invalid_card_number: 'El número de la tarjeta no es válido.',
    empty_card_holder_name: 'Ingresá el nombre del titular.',
    invalid_expiry_date: 'La fecha de vencimiento no es válida.',
    empty_token: 'No pudimos validar la tarjeta. Intentá de nuevo.',
    invalid_param: 'Revisá los datos de la tarjeta.',
};

const tokenErrorMessage = (data) => {
    const list = (data && data.error) || [];
    const type =
        Array.isArray(list) && list.length && list[0].error && list[0].error.type;
    return (
        TOKEN_ERRORS[type] ||
        'No pudimos validar la tarjeta. Revisá los datos e intentá de nuevo.'
    );
};

/* Tokeniza la tarjeta contra Payway y devuelve { token, bin }.
 *
 * El campo del token es `id`, NO `token`: la documentación del SDK dice
 * `{token: "..."}` pero la API responde `{id, status, bin, last_four_digits,
 * date_due, ...}`. Se aceptan los dos por las dudas.
 *
 * El `bin` sale de la respuesta en vez de recortar los primeros 6 dígitos del
 * número: es el que Payway asoció al token, y el cobro falla si no coinciden.
 *
 * El token vence a los 15 minutos (`date_due`), así que se pide al momento de
 * pagar y no antes. */
export const createCardToken = async ({
    environment,
    publicApiKey,
    cardNumber,
    expirationMonth,
    expirationYear,
    securityCode,
    cardHolderName,
    documentNumber,
    documentType = 'dni',
}) => {
    const base = BASE_URL[environment] || BASE_URL.sandbox;

    const response = await fetch(`${base}/api/v2/tokens`, {
        method: 'POST',
        headers: {
            apikey: publicApiKey,
            // Payway pide las dos: el SDK oficial manda ambas con la key pública.
            'X-Consumer-Username': publicApiKey,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            card_number: onlyDigits(cardNumber),
            card_expiration_month: String(expirationMonth).padStart(2, '0'),
            card_expiration_year: String(expirationYear).padStart(2, '0'),
            security_code: String(securityCode),
            card_holder_name: cardHolderName,
            card_holder_identification: {
                type: documentType,
                number: onlyDigits(documentNumber),
            },
        }),
    });

    let data = null;
    try {
        data = await response.json();
    } catch (err) {
        data = null;
    }

    const token = data && (data.id || data.token);
    if (!response.ok || !token) {
        throw new Error(tokenErrorMessage(data));
    }

    return { token, bin: (data && data.bin) || onlyDigits(cardNumber).slice(0, 6) };
};
